import { ownedNodes } from '@/simulation/native/authority';
import { LogicNodeDefinition } from './LogicNodeDefinition';
import { LogicNodeInstanceDto } from '@/schemas/logic';
import { logicNodeRegistry } from './LogicNodeRegistry';
import { deepClone } from '@/utils/clone';
import type { NodeHandle } from './RuntimeHandles';
import {
  standaloneRuntimeIds,
  type RuntimeGraphIds,
  type RuntimeNodeImport,
} from './RuntimeGraphIds';
import type { SimulationTiming } from './SimulationEventQueue';
import { DelayNode } from './nodes/DelayNode';
import {
  runtimeIdentity,
  runtimeIdentityText,
  type RuntimeIdentity,
} from './RuntimeIdentity';
import {
  cloneSavedDelayState,
  nodeSnapshotWallAt,
  type SavedTimeMapping,
} from './SavedNodeState';

// Timing provenance is private and weak; runtime records retain exactly their old fields.
interface CapturedRuntimeTiming {
  mapping: SavedTimeMapping;
  clockOwner?: object;
}
const capturedRuntimeTiming = new WeakMap<object, CapturedRuntimeTiming>();

/**
 * Logic graph node instance. NOT MobX-observable — graph nodes are created
 * and destroyed frequently during rebuilds and the MobX proxy overhead per
 * node dominated rebuild time for large circuits. Reactivity is handled at
 * the graph-reference level (LogicCircuitModel.logicGraph) instead.
 */
export class LogicNodeInstance<TState = any> {
  id: NodeHandle;
  private externalId?: string;
  type: string;
  state: TState;
  definition: LogicNodeDefinition<TState>;

  // Current output value
  output: boolean = false;
  /** Shared stable path components, independent of electrical graph handles. */
  runtimeIdentity?: RuntimeIdentity;
  /** Exact text adapter for files, checkpoints, extensions and diagnostics. */
  get runtimeKey(): string | undefined {
    return this.runtimeIdentity && runtimeIdentityText(this.runtimeIdentity);
  }
  set runtimeKey(value: string | undefined) {
    this.runtimeIdentity = value === undefined ? undefined : runtimeIdentity(value);
  }
  get hasRuntimeKey(): boolean {
    if (!hasNativeRuntimeIdentity(this)) return !!this.runtimeKey;
    return this.runtimeIdentity !== undefined && this.runtimeIdentity !== '';
  }
  get rootRuntimeKey(): string | undefined {
    if (!hasNativeRuntimeIdentity(this)) return this.runtimeKey;
    return typeof this.runtimeIdentity === 'string' ? this.runtimeIdentity : undefined;
  }
  /** Derived bundle observation, not an electrical transport boundary. */
  isBundleObserver?: boolean;
  /** Structured inferred transport path, preserved across chip boundaries. */
  bundlePath?: string[];
  /** Presentation paired with stable path atoms; never used for connectivity. */
  bundlePathLabels?: string[];
  /** Transport provenance excludes scalar-selector compatibility aliases. */
  bundleTransportIdentities?: string[][];
  cleanup?: () => void;
  // A live native owner factory becomes a detached numeric mapper on disposal.
  #savedTimeMappingFactory?: () => SavedTimeMapping;
  #clockOwner?: object;

  constructor(
    id: NodeHandle,
    definition: LogicNodeDefinition<TState>,
    readonly ids: RuntimeGraphIds = standaloneRuntimeIds,
  ) {
    this.id = id;
    this.type = definition.type;
    this.definition = definition;
    this.state = definition.createDefaultState();
  }

  static fromDto<TState>(
    dto: LogicNodeInstanceDto,
    importer: RuntimeNodeImport = standaloneRuntimeIds.importer(),
  ) {
    const definition = logicNodeRegistry.get(dto.type) as LogicNodeDefinition<TState>;
    const instance = new LogicNodeInstance(
      importer.node(dto.id),
      definition,
      importer.ids,
    );
    instance.externalId = dto.id;
    // Use saved state if valid, otherwise fall back to default to prevent null state crashes
    const savedState = dto.state != null ? dto.state : definition.createDefaultState();
    instance.state = deepClone(
      definition.stateSchema ? definition.stateSchema.parse(savedState) : savedState,
    ) as TState;
    instance.output = dto.output;
    // A restored snapshot has no physical pointer/key owner for a held button.
    if (instance.type === 'BUTTON') {
      (instance.state as { isOn: boolean }).isOn = false;
      instance.output = false;
    }
    instance.runtimeKey = dto.runtimeKey;
    return instance;
  }

  get dto(): LogicNodeInstanceDto {
    const owner = ownedNodes.get(this);
    if (owner && !owner.capturing)
      throw Error('Worker-owned node requires authoritative capture');
    return {
      id: this.idText,
      type: this.type,
      // Exports yield between chunks; preserve the state captured with this output.
      state: cloneNodeSavedState(this),
      output: this.output,
      runtimeKey: this.runtimeKey,
    };
  }

  /** Native capture only; the exported helper pins this implementation. */
  captureState(wallAt = nodeSnapshotWallAt()): TState {
    const factory =
      #savedTimeMappingFactory in this ? this.#savedTimeMappingFactory : undefined;
    const mapping = factory && this.definition === DelayNode ? factory() : undefined;
    return cloneSavedDelayState(this.state, mapping, wallAt);
  }

  /** Defining-module helper pins this implementation before external overrides. */
  static captureRuntimeTimeMapping(previous: unknown): SavedTimeMapping | undefined {
    if (
      !previous ||
      typeof previous !== 'object' ||
      !(#savedTimeMappingFactory in previous)
    )
      return undefined;
    const factory = previous.#savedTimeMappingFactory;
    return factory && previous.definition === DelayNode ? factory() : undefined;
  }

  /** The owner token is independent of elapsed-time generations and holds no simulation. */
  static captureRuntimeClockOwner(previous: unknown): object | undefined {
    return previous && typeof previous === 'object' && #clockOwner in previous
      ? previous.#clockOwner
      : undefined;
  }

  private retainSavedTimeFrom(previous: unknown) {
    const record =
      previous && typeof previous === 'object'
        ? capturedRuntimeTiming.get(previous)
        : undefined;
    const mapping = nativeRuntimeTimeMapping(previous) ?? record?.mapping;
    if (this.definition === DelayNode) {
      this.#savedTimeMappingFactory = mapping ? () => mapping : undefined;
      this.#clockOwner = mapping
        ? record
          ? record.clockOwner
          : nativeRuntimeClockOwner(previous)
        : undefined;
    }
  }

  get hasImportedIdentity(): boolean {
    return this.externalId !== undefined;
  }

  get idText(): string {
    return this.externalId ?? this.ids.nodeText(this.id);
  }

  restoreRuntimeState(previous: { type: string; state: any; output: boolean }) {
    if (this.type !== previous.type) return;
    this.retainSavedTimeFrom(previous);
    this.output = previous.output;
    if (
      this.state !== null &&
      typeof this.state === 'object' &&
      previous.state !== null &&
      typeof previous.state === 'object'
    ) {
      const configuration = new Set(this.definition.configurationKeys ?? []);
      for (const key of Object.keys(previous.state)) {
        if (!configuration.has(key))
          (this.state as Record<string, unknown>)[key] = previous.state[key];
      }
    } else this.state = previous.state;
  }

  setTrigger(trigger: () => void, timing?: SimulationTiming) {
    this.dispose();
    const nativeDelay = this.definition === DelayNode;
    // New or hydrated nodes are wall-domain values. Native clones/restorations
    // retain their old domain; convert only when crossing owner identities.
    // Copy before conversion so old graph/runtime state aliases stay untouched.
    if (nativeDelay && timing?.fromWallTime && timing.clockOwner !== this.#clockOwner) {
      const wallAt = nodeSnapshotWallAt();
      const oldMapping = this.#savedTimeMappingFactory?.();
      this.state = cloneSavedDelayState(
        this.state,
        (at, capturedWall) =>
          timing.fromWallTime!(
            oldMapping ? oldMapping(at, capturedWall) : at,
            capturedWall,
          ),
        wallAt,
      );
    }
    const capture = nativeDelay ? timing?.captureTimeMapping : undefined;
    this.#savedTimeMappingFactory = capture && timing ? capture.bind(timing) : undefined;
    this.#clockOwner = nativeDelay ? timing?.clockOwner : undefined;
    if (this.definition.setup) {
      const result = this.definition.setup(this.state, trigger, timing);
      if (result) this.cleanup = result;
    }
  }

  /** Exact native restore: raw Delay deadlines already belong to the saved logical domain. */
  restoreContinuationTrigger(
    trigger: () => void,
    timing: SimulationTiming,
    attach = true,
  ) {
    this.dispose();
    this.#clockOwner = this.definition === DelayNode ? timing.clockOwner : undefined;
    const capture = this.definition === DelayNode ? timing.captureTimeMapping : undefined;
    this.#savedTimeMappingFactory = capture ? capture.bind(timing) : undefined;
    const cleanup = attach
      ? this.definition.setup?.(this.state, trigger, timing)
      : undefined;
    if (cleanup) this.cleanup = cleanup;
  }

  dispose() {
    // Freeze BEFORE cleanup removes runtime timing; keep no owner closure after detach.
    try {
      const mapping = this.#savedTimeMappingFactory?.();
      if (mapping) this.#savedTimeMappingFactory = () => mapping;
    } finally {
      this.cleanup?.();
      this.cleanup = undefined;
    }
  }

  clone(newId?: NodeHandle): LogicNodeInstance<TState> {
    const id = newId ?? this.ids.scope.allocateNode();
    const cloned = new LogicNodeInstance<TState>(id, this.definition, this.ids);
    cloned.state = deepClone(this.state);
    cloned.retainSavedTimeFrom(this);
    cloned.output = this.output;
    if (!hasNativeRuntimeIdentity(this)) cloned.runtimeKey = this.runtimeKey;
    else cloned.runtimeIdentity = this.runtimeIdentity;
    cloned.isBundleObserver = this.isBundleObserver;
    cloned.bundlePath = this.bundlePath && [...this.bundlePath];
    cloned.bundlePathLabels = this.bundlePathLabels && [...this.bundlePathLabels];
    cloned.bundleTransportIdentities = this.bundleTransportIdentities?.map((ids) => [
      ...ids,
    ]);
    return cloned;
  }

  process(input: boolean, deltaTime: number) {
    this.output = this.definition.process(input, this.state, deltaTime);
  }
}

const nativeRuntimeKeyAccessors = Object.getOwnPropertyDescriptor(
  LogicNodeInstance.prototype,
  'runtimeKey',
)!;
export function hasNativeRuntimeIdentityHooks(): boolean {
  const current = Object.getOwnPropertyDescriptor(
    LogicNodeInstance.prototype,
    'runtimeKey',
  );
  return (
    current?.get === nativeRuntimeKeyAccessors.get &&
    current?.set === nativeRuntimeKeyAccessors.set
  );
}
export function hasNativeRuntimeIdentity(
  node: LogicNodeInstance,
  hooks = hasNativeRuntimeIdentityHooks(),
): boolean {
  return (
    hooks &&
    Object.getPrototypeOf(node) === LogicNodeInstance.prototype &&
    !Object.hasOwn(node, 'runtimeKey')
  );
}

// Compact native capture previously cloned state directly. Do not start invoking
// arbitrary newly-added captureState overrides or user dto getters there.
const nativeCaptureState = LogicNodeInstance.prototype.captureState;
export function cloneNodeSavedState<T>(
  node: LogicNodeInstance<T>,
  wallAt = nodeSnapshotWallAt(),
): T {
  return nativeCaptureState.call(node, wallAt) as T;
}

const nativeRuntimeTimeMapping = LogicNodeInstance.captureRuntimeTimeMapping;
const nativeRuntimeClockOwner = LogicNodeInstance.captureRuntimeClockOwner;
/** Preserve the historical copied output/type plus aliased state across rebuild yields. */
export function captureNodeRuntimeState<T>(node: LogicNodeInstance<T>) {
  const record = { output: node.output, state: node.state, type: node.type };
  const mapping = nativeRuntimeTimeMapping(node);
  if (mapping)
    capturedRuntimeTiming.set(record, {
      mapping,
      clockOwner: nativeRuntimeClockOwner(node),
    });
  return record;
}
